import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './Dashboard.css';
import Overview from './Overview';
import SalesActivity from './SalesActivity';
import DailyLogger from './DailyLogger';
import AnalyticsNew from './AnalyticsNew';
import Dealerships from './Dealerships';
import UserManagement from './UserManagement';
import GmDropdowns from './GmDropdowns';
import AdminPanel from './AdminPanel';
import Targets from './Targets';
import PartsInput from './PartsInput';
import ServiceInput from './ServiceInput';
import ApparelInput from './ApparelInput';
import InventoryInput from './InventoryInput';
import SalesPerformanceDashboard from './SalesPerformanceDashboard';
import FixedOpsPerformanceDashboard from './FixedOpsPerformanceDashboard';
import SalesScoreboardPage from './SalesScoreboardPage';
import FixedOpsScoreboardPage from './FixedOpsScoreboardPage';
import VisualSalesScoreboardPage from './VisualSalesScoreboardPage';
import SalesLeaderScoreboardPage from './SalesLeaderScoreboardPage';
import FIScoreboardPage from './FIScoreboardPage';
import ProfitSummaryPage from './ProfitSummaryPage';
import NPSInput from './NPSInput';
import UserPreferences from './UserPreferences';
import HelpCenter from './HelpCenter';
import Changelog from './Changelog';

export default function Dashboard({ user, onLogout, onUserUpdate, impersonator, onImpersonate, onStopImpersonation }) {
  const navigate = useNavigate();
  const location = useLocation();
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';
  
  // Derive activeView from URL path
  const getViewFromPath = (pathname) => {
    const path = pathname.substring(1); // Remove leading slash
    if (!path) return (user?.role === 'ADMIN' || user?.role === 'SITE_MANAGER') ? 'admin' : 'overview';
    return path;
  };
  
  const [activeView, setActiveView] = useState(() => getViewFromPath(location.pathname));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [permissionsVersion, setPermissionsVersion] = useState(0);
  const [managedOwner, setManagedOwner] = useState(null); // Owner being managed by admin
  const [notifications, setNotifications] = useState([]);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const notificationPanelRef = useRef(null);
  const userMenuRef = useRef(null);

  // Sync activeView with URL changes
  useEffect(() => {
    const view = getViewFromPath(location.pathname);
    setActiveView(view);
  }, [location.pathname]);

  // Helper to navigate and close mobile menu
  const navigateTo = (path) => {
    navigate(`/${path}`);
    setActiveView(path);
    setMobileMenuOpen(false);
  };

  // Enter managed-owner mode: set context then navigate once (not in an effect,
  // which would re-fire whenever navigate's reference changes after each navigation)
  const handleManageOwner = useCallback((owner) => {
    setManagedOwner(owner);
    navigate('/overview');
    setActiveView('overview');
  }, [navigate]);

  const initials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((p) => p[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();
  };

  // Listen for storage changes from other tabs/windows
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'rolePermissions') {
        setPermissionsVersion(prev => prev + 1);
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Custom event for same-tab updates
  useEffect(() => {
    const handlePermissionsUpdate = () => {
      setPermissionsVersion(prev => prev + 1);
    };
    window.addEventListener('rolePermissionsUpdated', handlePermissionsUpdate);
    return () => window.removeEventListener('rolePermissionsUpdated', handlePermissionsUpdate);
  }, []);

  const handleNotificationsChange = useCallback((newNotifications) => {
    setNotifications(newNotifications);
  }, []);

  // Close notification panel on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notificationPanelRef.current && !notificationPanelRef.current.contains(e.target)) {
        setNotificationsOpen(false);
      }
    };
    if (notificationsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [notificationsOpen]);

  // Close user menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userMenuOpen]);

  const isAdmin = user?.role === 'ADMIN' && !managedOwner;
  // Platform staff (super admin + site manager) reach the management panel.
  const isPlatformStaff = (user?.role === 'ADMIN' || user?.role === 'SITE_MANAGER') && !managedOwner;
  const isOwner = user?.role === 'OWNER' || managedOwner; // Treat managed owner view as owner mode
  const isDealership = user?.role === 'MANAGER' || user?.role === 'EMPLOYEE';

  // Platform staff have no owner context outside managed-owner mode (managedOwner
  // lives in memory only, so a page refresh clears it); send them back to the
  // admin panel instead of crashing on an owner view.
  useEffect(() => {
    const staffViews = ['admin', 'profile', 'help', 'changelog'];
    if (isPlatformStaff && !staffViews.includes(activeView)) {
      setActiveView('admin');
      navigate('/admin', { replace: true });
    }
  }, [isPlatformStaff, activeView, navigate]);
  const userRoles = Array.isArray(user?.roles) ? user.roles : [];
  const userRolesKey = JSON.stringify(userRoles); // Serialize for memo dependency

  // Fetch global role permissions from backend
  const [rolePermissions, setRolePermissions] = useState(null);
  const [permissionsLoading, setPermissionsLoading] = useState(true);

  useEffect(() => {
    const fetchRolePermissions = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = {};
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        console.log('[DASHBOARD] Fetching global role permissions');
        const response = await fetch(
          `${API_URL}/api/dealerships/role-permissions`,
          { credentials: 'include', headers }
        );

        if (response.ok) {
          const data = await response.json();
          console.log('[DASHBOARD] Fetched global role permissions from API:', data);
          console.log('[DASHBOARD] Setting rolePermissions to:', data.rolePermissions);
          setRolePermissions(data.rolePermissions || {});
        } else {
          console.log('[DASHBOARD] Failed to fetch role permissions, status:', response.status);
        }
      } catch (err) {
        console.error('Error fetching role permissions:', err);
      } finally {
        setPermissionsLoading(false);
      }
    };

    fetchRolePermissions();

    // Listen for role permission updates
    const handlePermissionsUpdated = () => {
      fetchRolePermissions();
    };

    window.addEventListener('rolePermissionsUpdated', handlePermissionsUpdated);
    return () => window.removeEventListener('rolePermissionsUpdated', handlePermissionsUpdated);
  }, []);
  
  // DEBUG: Log user object and roles
  console.log('=== DASHBOARD USER DEBUG ===');
  console.log('Full user object:', user);
  console.log('user.roles:', user?.roles);
  console.log('userRoles array:', userRoles);
  console.log('user.role (system):', user?.role);
  console.log('rolePermissions:', rolePermissions);
  console.log('=========================');
  
  // Compute which beta features are enabled for the current user's dealership(s).
  // For OWNER users, a feature is considered enabled if ANY of their dealerships has it on.
  // For MANAGER/EMPLOYEE users, it comes directly from their primary dealership.
  const dealershipBetaFeatures = useMemo(() => {
    if (user?.role === 'ADMIN') return { insights: true, visualSalesDashboard: true, talonApi: true, salesScoreboard: true };
    if (user?.betaFeatures) return user.betaFeatures;
    if (Array.isArray(user?.dealerships) && user.dealerships.length > 0) {
      // Merge: any dealership enabling a feature turns it on for the owner
      return user.dealerships.reduce((acc, d) => {
        const bf = d.betaFeatures || {};
        Object.keys(bf).forEach(k => { if (bf[k]) acc[k] = true; });
        return acc;
      }, {});
    }
    return {};
  }, [user]);

  // Calculate feature access - recalculates when rolePermissions changes
  const featureAccess = useMemo(() => {
    // Debug logging
    console.log('Dashboard Feature Access Calculation:', {
      userRole: user?.role,
      userRoles: userRoles,
      isOwner: isOwner,
      rolePermissions: rolePermissions
    });

    // Helper to check if a role has access to a feature
    const hasFeatureAccess = (featureId) => {
      // Critical features that OWNER system role always has access to (cannot be hidden)
      const criticalFeatures = ['user-management', 'dealerships'];
      if (user?.role === 'OWNER' && criticalFeatures.includes(featureId)) {
        console.log(`✓ Owner role has access to critical feature "${featureId}"`);
        return true;
      }

      // For OWNER system role, check "Owner" job role permissions to allow hiding non-critical features
      if (user?.role === 'OWNER') {
        console.log(`[OWNER CHECK] Checking feature "${featureId}"`, {
          rolePermissions,
          hasOwnerKey: rolePermissions?.hasOwnProperty('Owner'),
          ownerPermissions: rolePermissions?.['Owner']
        });

        // If permissions are configured and "Owner" job role exists, check it
        if (rolePermissions && rolePermissions['Owner']) {
          const hasAccess = rolePermissions['Owner'].includes(featureId);
          console.log(`${hasAccess ? '✓' : '✗'} Owner job role ${hasAccess ? 'shows' : 'hides'} "${featureId}"`);
          return hasAccess;
        }
        // If no permissions configured, show everything by default
        console.log(`✓ Owner role has access to "${featureId}" (no permissions configured)`);
        return true;
      }

      // Dashboard system role gets default scoreboard access
      if (user?.role === 'DASHBOARD') {
        const dashboardFeatures = ['overview', 'sales-scoreboard', 'fixedops-scoreboard'];
        if (dashboardFeatures.includes(featureId)) {
          console.log(`✓ Dashboard system role has access to "${featureId}"`);
          return true;
        }
      }

      // If no permissions configured, deny access (admin must configure)
      if (!rolePermissions || Object.keys(rolePermissions).length === 0) {
        console.log(`✗ No rolePermissions configured, denying "${featureId}" (admin must configure ManageRoles)`);
        return false;
      }

      // Check if any of the user's assigned dealership roles have this feature
      if (!Array.isArray(userRoles) || userRoles.length === 0) {
        console.log(`✗ User has no dealership roles assigned`);
        return false;
      }

      for (const role of userRoles) {
        if (rolePermissions[role]?.includes(featureId)) {
          console.log(`✓ Role "${role}" has access to "${featureId}"`);
          return true;
        }
      }

      console.log(`✗ Feature "${featureId}" denied (roles "${userRoles.join(', ')}" not configured or unchecked)`);
      return false;
    };
    
    // Owner-only features (always available to Owner, denied to non-owners)
    const isOwnerOnlyFeature = (featureId) => {
      return ['dealerships', 'user-management'].includes(featureId);
    };
    
    const access = {
      // Regular features - check role permissions
      hasOverview: managedOwner || hasFeatureAccess('overview'),
      // Beta-gated: only show if the dealership has enabled these features
      hasInsights: (dealershipBetaFeatures.insights) && (managedOwner || hasFeatureAccess('insights')),
      hasSalesActivity: managedOwner || hasFeatureAccess('sales-activity'),
      hasDailyLog: managedOwner || hasFeatureAccess('daily-logger'),
      hasPartsAccess: managedOwner || hasFeatureAccess('parts-entry'),
      hasServiceAccess: managedOwner || hasFeatureAccess('service-entry'),
      hasApparelAccess: managedOwner || hasFeatureAccess('apparel-entry'),
      hasInventoryAccess: managedOwner || hasFeatureAccess('inventory-entry'),

      // Owner-only features - require isOwner AND managedOwner fallback
      hasDealerships: managedOwner || isOwner,
      hasUserManagement: managedOwner || isOwner,
      hasSalesPerformance: managedOwner || hasFeatureAccess('sales-performance'),
      hasFixedOpsPerformance: managedOwner || hasFeatureAccess('fixed-ops-performance'),
      hasSalesScoreboard: (dealershipBetaFeatures.salesScoreboard) && (managedOwner || hasFeatureAccess('sales-scoreboard')),
      // Beta-gated: only show if the dealership has enabled this feature
      hasVisualSalesScoreboard: (dealershipBetaFeatures.visualSalesDashboard) && (managedOwner || hasFeatureAccess('visual-sales-scoreboard')),
      hasSalesLeaderScoreboard: managedOwner || hasFeatureAccess('sales-leader-scoreboard'),
      hasFixedOpsScoreboard: managedOwner || hasFeatureAccess('fixedops-scoreboard'),
      hasFiScoreboard: managedOwner || hasFeatureAccess('fi-scoreboard'),
      hasProfitSummary: managedOwner || hasFeatureAccess('profit-summary'),
      hasNPSAccess: managedOwner || hasFeatureAccess('nps-input'),

      // Dealership-scoped management permissions (e.g. General Manager). Owners
      // already have these via isOwner; these flags light them up for permitted
      // non-owner roles, scoped to their dealership(s) by the backend.
      hasManageUsers: hasFeatureAccess('manage-users'),
      hasEditDropdowns: hasFeatureAccess('edit-dropdowns')
    };
    
    console.log('Calculated feature access:', access);
    return access;
  }, [rolePermissions, isOwner, userRolesKey, managedOwner, dealershipBetaFeatures]);
  
  const { hasSalesActivity, hasDailyLog, hasOverview, hasInsights, hasDealerships, hasUserManagement, hasPartsAccess, hasServiceAccess, hasApparelAccess, hasInventoryAccess, hasSalesPerformance, hasFixedOpsPerformance, hasSalesScoreboard, hasVisualSalesScoreboard, hasSalesLeaderScoreboard, hasFixedOpsScoreboard, hasFiScoreboard, hasProfitSummary, hasNPSAccess, hasManageUsers, hasEditDropdowns } = featureAccess;

  // Who sees the activity alert bell ("has everyone logged?"): owners, managed-owner
  // views, and GMs granted either dealership-management permission (Manage Users or
  // Edit Dropdowns). Passed to Overview so the bell and its data stay in sync.
  const canSeeActivityAlerts = isOwner || hasManageUsers || hasEditDropdowns;

  // Redirect to first accessible view if the current view isn't permitted
  useEffect(() => {
    if (permissionsLoading) return;
    const viewPriority = [
      ['overview', hasOverview],
      ['sales', hasSalesActivity],
      ['daily', hasDailyLog],
      ['sales-performance', hasSalesPerformance],
      ['fixed-ops-performance', hasFixedOpsPerformance],
      ['profit-summary', hasProfitSummary],
      ['fixedops-scoreboard', hasFixedOpsScoreboard],
      ['sales-scoreboard', hasSalesScoreboard],
      ['sales-leader-scoreboard', hasSalesLeaderScoreboard],
      ['fi-scoreboard', hasFiScoreboard],
      ['parts', hasPartsAccess],
      ['service', hasServiceAccess],
      ['apparel', hasApparelAccess],
      ['inventory', hasInventoryAccess],
      ['nps-input', hasNPSAccess],
    ];
    const currentEntry = viewPriority.find(([view]) => view === activeView);
    if (currentEntry && !currentEntry[1]) {
      const first = viewPriority.find(([, hasAccess]) => hasAccess);
      if (first) navigateTo(first[0]);
    }
  }, [permissionsLoading, featureAccess]); // eslint-disable-line

  const renderView = () => {
    switch (activeView) {
      case 'admin':
        return <AdminPanel user={user} onManageOwner={handleManageOwner} onImpersonate={onImpersonate} />;
      case 'overview':
        return <Overview user={user} managedOwner={managedOwner} onNotificationsChange={handleNotificationsChange} showActivityAlerts={canSeeActivityAlerts} />;
      case 'sales':
        return <SalesActivity user={user} managedOwner={managedOwner} />;
      case 'daily':
        return <DailyLogger user={user} managedOwner={managedOwner} />;
      case 'parts':
        return <PartsInput user={user} managedOwner={managedOwner} />;
      case 'service-input':
        return <ServiceInput user={user} managedOwner={managedOwner} />;
      case 'apparel':
        return <ApparelInput user={user} managedOwner={managedOwner} />;
      case 'inventory':
        return <InventoryInput user={user} managedOwner={managedOwner} />;
      case 'nps-input':
        return <NPSInput user={user} managedOwner={managedOwner} />;
      case 'insights':
        return <AnalyticsNew user={user} managedOwner={managedOwner} />;
      case 'sales-performance':
        return <SalesPerformanceDashboard user={user} managedOwner={managedOwner} />;
      case 'fixed-ops-performance':
        return <FixedOpsPerformanceDashboard user={user} managedOwner={managedOwner} />;
      case 'sales-scoreboard':
        return <SalesScoreboardPage user={user} managedOwner={managedOwner} />;
      case 'visual-sales-scoreboard':
        return <VisualSalesScoreboardPage user={user} managedOwner={managedOwner} />;
      case 'sales-leader-scoreboard':
        return <SalesLeaderScoreboardPage user={user} managedOwner={managedOwner} />;
      case 'fixedops-scoreboard':
        return <FixedOpsScoreboardPage user={user} managedOwner={managedOwner} />;
      case 'fi-scoreboard':
        return <FIScoreboardPage user={user} managedOwner={managedOwner} />;
      case 'profit-summary':
        return <ProfitSummaryPage user={user} managedOwner={managedOwner} />;
      case 'dealerships':
        return <Dealerships user={user} managedOwner={managedOwner} />;
      case 'users':
        return <UserManagement user={user} managedOwner={managedOwner} dealershipBetaFeatures={dealershipBetaFeatures} onImpersonate={onImpersonate} />;
      case 'dropdowns':
        return <GmDropdowns user={user} />;
      case 'targets':
        return <Targets user={user} managedOwner={managedOwner} />;
      case 'profile':
        return <UserPreferences user={user} onUserUpdate={onUserUpdate} />;
      case 'help':
        return <HelpCenter user={user} />;
      case 'changelog':
        return <Changelog user={user} />;
      default:
        return <Overview user={user} managedOwner={managedOwner} onNotificationsChange={handleNotificationsChange} showActivityAlerts={canSeeActivityAlerts} />;
    }
  };

  return (
    <div className="dashboard-container">
      {/* Mobile hamburger button */}
      <button 
        className="mobile-menu-toggle"
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        aria-label="Toggle menu"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="3" y1="12" x2="21" y2="12"/>
          <line x1="3" y1="6" x2="21" y2="6"/>
          <line x1="3" y1="18" x2="21" y2="18"/>
        </svg>
      </button>

      {/* Overlay for mobile menu */}
      {mobileMenuOpen && (
        <div 
          className="mobile-menu-overlay"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Notification Bell — owners, managed-owner views, and GMs with a management permission */}
      {(canSeeActivityAlerts) && (
        <div className="notification-bell-container" ref={notificationPanelRef}>
          <button
            className="notification-bell-btn"
            onClick={() => setNotificationsOpen(o => !o)}
            aria-label="Notifications"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
              <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
            </svg>
            {notifications.some(n => n.type === 'warning') && (
              <span className="notification-badge" />
            )}
          </button>

          {notificationsOpen && (
            <div className="notification-panel">
              <div className="notification-panel-header">
                <span>Notifications</span>
                <button onClick={() => setNotificationsOpen(false)} aria-label="Close">✕</button>
              </div>
              {notifications.length === 0 ? (
                <div className="notification-empty">No notifications</div>
              ) : notifications.map(n => (
                <div key={n.id} className={`notification-item notification-item-${n.type}`}>
                  <div className="notification-item-title">
                    {n.type === 'warning' ? '⚠️' : '✅'} {n.title}
                  </div>
                  {n.message && (
                    <div className="notification-item-message">{n.message}</div>
                  )}
                  {n.dealershipStatus && (
                    <table className="notification-status-table">
                      <thead>
                        <tr>
                          <th>Dealership</th>
                          <th>Sales</th>
                          <th>Fixed Ops</th>
                          <th>Inventory</th>
                        </tr>
                      </thead>
                      <tbody>
                        {n.dealershipStatus.map(d => (
                          <tr key={d.name}>
                            <td>{d.name}</td>
                            <td style={{ textAlign: 'center' }}>
                              {d.salesLogged
                                ? <span style={{ color: '#10b981', fontWeight: 700 }}>✔</span>
                                : <span style={{ color: '#ef4444', fontWeight: 700 }}>✗</span>}
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              {d.fixedOpsLogged
                                ? <span style={{ color: '#10b981', fontWeight: 700 }}>✔</span>
                                : <span style={{ color: '#ef4444', fontWeight: 700 }}>✗</span>}
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              {d.inventoryLogged
                                ? <span style={{ color: '#10b981', fontWeight: 700 }}>✔</span>
                                : <span style={{ color: '#ef4444', fontWeight: 700 }}>✗</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <aside className={`dashboard-sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          <img src="/MP%20Full%20Color@3x.png" alt="Masters Path" className="sidebar-logo-img" />
          <h1 className="app-title">Performance Dashboard</h1>
        </div>

        <nav className="sidebar-nav">
          {isPlatformStaff && (
            <button
              className={activeView === 'admin' ? 'nav-item active' : 'nav-item'}
              onClick={() => navigateTo('admin')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                <path d="M2 17l10 5 10-5"/>
                <path d="M2 12l10 5 10-5"/>
              </svg>
              Admin Panel
            </button>
          )}

          {isPlatformStaff && (
            <button
              className={activeView === 'changelog' ? 'nav-item active' : 'nav-item'}
              onClick={() => navigateTo('changelog')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 8v4l3 3"/>
                <circle cx="12" cy="12" r="9"/>
              </svg>
              What&rsquo;s New
            </button>
          )}

          {hasOverview && (
            <button 
              className={activeView === 'overview' ? 'nav-item active' : 'nav-item'}
              onClick={() => navigateTo('overview')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="7" height="7"/>
                <rect x="14" y="3" width="7" height="7"/>
                <rect x="14" y="14" width="7" height="7"/>
                <rect x="3" y="14" width="7" height="7"/>
              </svg>
              Overview
            </button>
          )}

          {hasInsights && (
            <button 
              className={activeView === 'insights' ? 'nav-item active' : 'nav-item'}
              onClick={() => navigateTo('insights')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="20" x2="18" y2="10"/>
                <line x1="12" y1="20" x2="12" y2="4"/>
                <line x1="6" y1="20" x2="6" y2="14"/>
              </svg>
              Insights
            </button>
          )}

          {/* Loggers Group */}
          {(hasSalesActivity || hasDailyLog || hasPartsAccess || hasServiceAccess || hasApparelAccess || hasInventoryAccess || hasNPSAccess) && (
            <div className="menu-group">
              <div className="menu-group-label">Loggers</div>
              
              {hasSalesActivity && (
                <button 
                  className={activeView === 'sales' ? 'nav-item active' : 'nav-item'}
                  onClick={() => navigateTo('sales')}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                  </svg>
                  F&I Input
                </button>
              )}

              {hasDailyLog && (
                <button 
                  className={activeView === 'daily' ? 'nav-item active' : 'nav-item'}
                  onClick={() => navigateTo('daily')}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="4" width="18" height="18" rx="2"/>
                    <path d="M16 2v4M8 2v4M3 10h18"/>
                  </svg>
                  10:4 Sales Process
                </button>
              )}

              {hasPartsAccess && (
                <button 
                  className={activeView === 'parts' ? 'nav-item active' : 'nav-item'}
                  onClick={() => navigateTo('parts')}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z"/>
                    <path d="M19.4 15a1.6 1.6 0 0 0 .33 1.8l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.6 1.6 0 0 0-1.8-.33 1.6 1.6 0 0 0-1 1.5V21a2 2 0 0 1-4 0v-.09a1.6 1.6 0 0 0-1-1.5 1.6 1.6 0 0 0-1.8.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.6 1.6 0 0 0 .33-1.8 1.6 1.6 0 0 0-1.5-1H3a2 2 0 0 1 0-4h.09a1.6 1.6 0 0 0 1.5-1 1.6 1.6 0 0 0-.33-1.8l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.6 1.6 0 0 0 1.8.33h.09a1.6 1.6 0 0 0 1-1.5V3a2 2 0 0 1 4 0v.09a1.6 1.6 0 0 0 1 1.5h.09a1.6 1.6 0 0 0 1.8-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.6 1.6 0 0 0-.33 1.8v.09a1.6 1.6 0 0 0 1.5 1H21a2 2 0 0 1 0 4h-.09a1.6 1.6 0 0 0-1.51 1Z"/>
                  </svg>
                  Parts & Accessories
                </button>
              )}

              {hasServiceAccess && (
                <button 
                  className={activeView === 'service-input' ? 'nav-item active' : 'nav-item'}
                  onClick={() => navigateTo('service-input')}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
                  </svg>
                  Service Input
                </button>
              )}

              {hasApparelAccess && (
                <button 
                  className={activeView === 'apparel' ? 'nav-item active' : 'nav-item'}
                  onClick={() => navigateTo('apparel')}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3.5 12.5c0-4.97 4.03-9 9-9h1.5A5 5 0 0 1 19 8.5V11h-3.7a1.8 1.8 0 1 0 0 3.6H19v1.4A3.9 3.9 0 0 1 15.1 20H12c-4.97 0-8.5-3.53-8.5-7.5Z"/>
                    <path d="M15 9.5H20"/>
                  </svg>
                  Apparel & Licensing
                </button>
              )}

              {hasInventoryAccess && (
                <button
                  className={activeView === 'inventory' ? 'nav-item active' : 'nav-item'}
                  onClick={() => navigateTo('inventory')}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="2" width="18" height="20" rx="2"/>
                    <line x1="12" y1="8" x2="12" y2="16"/>
                    <line x1="7" y1="12" x2="17" y2="12"/>
                  </svg>
                  Weekly Inventory
                </button>
              )}

              {hasNPSAccess && (
                <button
                  className={activeView === 'nps-input' ? 'nav-item active' : 'nav-item'}
                  onClick={() => navigateTo('nps-input')}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2z"/>
                    <path d="M8 14s1.5 2 4 2 4-2 4-2"/>
                    <line x1="9" y1="9" x2="9.01" y2="9"/>
                    <line x1="15" y1="9" x2="15.01" y2="9"/>
                  </svg>
                  NPS Input
                </button>
              )}
            </div>
          )}

          {/* Performance Dashboards Group */}
          {(hasSalesPerformance || hasFixedOpsPerformance || hasProfitSummary) && (
          <div className="menu-group">
            <div className="menu-group-label">Performance Dashboards</div>
            
            {hasSalesPerformance && (
              <button 
                className={activeView === 'sales-performance' ? 'nav-item active' : 'nav-item'}
                onClick={() => navigateTo('sales-performance')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                </svg>
                Sales Performance
              </button>
            )}
            {hasSalesScoreboard && (
              <button
                className={activeView === 'sales-scoreboard' ? 'nav-item active' : 'nav-item'}
                onClick={() => navigateTo('sales-scoreboard')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="4"/>
                  <path d="M7 7h10v10H7z"/>
                </svg>
                Sales Scoreboard
              </button>
            )}
            {hasVisualSalesScoreboard && (
              <button
                className={activeView === 'visual-sales-scoreboard' ? 'nav-item active' : 'nav-item'}
                onClick={() => navigateTo('visual-sales-scoreboard')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 3v18h18"/>
                  <path d="M18 17V9"/>
                  <path d="M13 17V5"/>
                  <path d="M8 17v-3"/>
                </svg>
                Visual Sales Dashboard
              </button>
            )}
            {hasSalesLeaderScoreboard && (
              <button
                className={activeView === 'sales-leader-scoreboard' ? 'nav-item active' : 'nav-item'}
                onClick={() => navigateTo('sales-leader-scoreboard')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="8.5" cy="7" r="4"/>
                  <polyline points="17 11 19 13 23 9"/>
                </svg>
                Sales Leader Scoreboard
              </button>
            )}
            {hasFixedOpsPerformance && (
              <button
                className={activeView === 'fixed-ops-performance' ? 'nav-item active' : 'nav-item'}
                onClick={() => navigateTo('fixed-ops-performance')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
                </svg>
                Fixed Operations Performance
              </button>
            )}
            {hasFixedOpsScoreboard && (
              <button
                className={activeView === 'fixedops-scoreboard' ? 'nav-item active' : 'nav-item'}
                onClick={() => navigateTo('fixedops-scoreboard')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="4"/>
                  <path d="M7 7h10v10H7z"/>
                </svg>
                Fixed Ops Scoreboard
              </button>
            )}
            {hasFiScoreboard && (
              <button
                className={activeView === 'fi-scoreboard' ? 'nav-item active' : 'nav-item'}
                onClick={() => navigateTo('fi-scoreboard')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2l2.9 6.3L22 9l-5 5 1.2 7-6.2-3.5L5.8 21 7 14 2 9l7.1-.7z"/>
                </svg>
                F&amp;I Scoreboard
              </button>
            )}

            {hasProfitSummary && (
              <button 
                className={activeView === 'profit-summary' ? 'nav-item active' : 'nav-item'}
                onClick={() => navigateTo('profit-summary')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
                  <polyline points="17 6 23 6 23 12"/>
                </svg>
                Performance Metrics
              </button>
            )}
          </div>
          )}

          {/* Settings Group — owners get the full set; a General Manager sees only
              the management surfaces they've been granted (scoped to their dealership) */}
          {(isOwner || hasManageUsers || hasEditDropdowns) && (
            <div className="menu-group">
              <div className="menu-group-label">Settings</div>

              {isOwner && hasDealerships && (
                <button
                  className={activeView === 'dealerships' ? 'nav-item active' : 'nav-item'}
                  onClick={() => navigateTo('dealerships')}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                    <polyline points="9 22 9 12 15 12 15 22"/>
                  </svg>
                  Dealerships
                </button>
              )}

              {((isOwner && hasUserManagement) || hasManageUsers) && (
                <button
                  className={activeView === 'users' ? 'nav-item active' : 'nav-item'}
                  onClick={() => navigateTo('users')}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                    <circle cx="9" cy="7" r="4"/>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                  </svg>
                  User Management
                </button>
              )}

              {/* Standalone dropdown editor for non-owner roles (GM). Owners edit
                  dropdowns inside the Dealerships page. */}
              {!isOwner && hasEditDropdowns && (
                <button
                  className={activeView === 'dropdowns' ? 'nav-item active' : 'nav-item'}
                  onClick={() => navigateTo('dropdowns')}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="8" y1="6" x2="21" y2="6"/>
                    <line x1="8" y1="12" x2="21" y2="12"/>
                    <line x1="8" y1="18" x2="21" y2="18"/>
                    <line x1="3" y1="6" x2="3.01" y2="6"/>
                    <line x1="3" y1="12" x2="3.01" y2="12"/>
                    <line x1="3" y1="18" x2="3.01" y2="18"/>
                  </svg>
                  Dealership Dropdowns
                </button>
              )}

              {isOwner && (
                <button
                  className={activeView === 'targets' ? 'nav-item active' : 'nav-item'}
                  onClick={() => {
                  navigateTo('targets');
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <circle cx="12" cy="12" r="6"/>
                    <circle cx="12" cy="12" r="2"/>
                  </svg>
                  Targets
                </button>
              )}
            </div>
          )}
        </nav>

        <div className="sidebar-footer">
          <div className="user-menu-container" ref={userMenuRef}>
            {userMenuOpen && (
              <div className="user-menu-popup">
                <button
                  className="user-menu-item"
                  onClick={() => { setUserMenuOpen(false); navigateTo('profile'); }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="8" r="4"/>
                    <path d="M20 21a8 8 0 1 0-16 0"/>
                  </svg>
                  Account Preferences
                </button>
                <button
                  className="user-menu-item"
                  onClick={() => { setUserMenuOpen(false); navigateTo('help'); }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                    <circle cx="12" cy="17" r="1" fill="currentColor" stroke="none"/>
                  </svg>
                  Help &amp; Guides
                </button>
                <button
                  className="user-menu-item"
                  onClick={() => { setUserMenuOpen(false); window.open('/support', '_blank', 'noopener,noreferrer'); }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <circle cx="12" cy="12" r="4"/>
                    <line x1="4.93" y1="4.93" x2="9.17" y2="9.17"/>
                    <line x1="14.83" y1="14.83" x2="19.07" y2="19.07"/>
                    <line x1="14.83" y1="9.17" x2="19.07" y2="4.93"/>
                    <line x1="4.93" y1="19.07" x2="9.17" y2="14.83"/>
                  </svg>
                  Support
                </button>
              </div>
            )}
            <button
              className="user-info user-info--btn"
              onClick={() => setUserMenuOpen(o => !o)}
              aria-label="Open user menu"
              aria-expanded={userMenuOpen}
            >
              <div className="user-avatar">{initials(user?.name)}</div>
              <div className="user-details">
                <div className="user-name">{user?.name}</div>
                <div className="user-role">{user?.role}</div>
              </div>
              <svg
                className={`user-menu-chevron${userMenuOpen ? ' open' : ''}`}
                width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
              >
                <polyline points="18 15 12 9 6 15"/>
              </svg>
            </button>
          </div>
          <button className="logout-btn" onClick={onLogout}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
              <polyline points="16 17 21 12 16 7"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
            Logout
          </button>
        </div>
      </aside>

      <main className="dashboard-main">
        {impersonator && (
          <div className="admin-proxy-bar impersonation-bar">
            <div className="proxy-bar-content">
              <span className="proxy-bar-text">
                👤 Viewing as <strong>{user?.name || user?.email}</strong>
                {user?.role ? ` (${user.role})` : ''}
              </span>
              <button
                className="proxy-bar-close"
                onClick={onStopImpersonation}
                title="Return to your admin account"
              >
                ← Return to {impersonator.name || 'admin'}
              </button>
            </div>
          </div>
        )}
        {managedOwner && !impersonator && (
          <div className="admin-proxy-bar">
            <div className="proxy-bar-content">
              <span className="proxy-bar-text">
                {user?.role === 'SITE_MANAGER' ? 'Site Manager' : 'Admin'} viewing: <strong>{managedOwner.name}</strong>
              </span>
              <button
                className="proxy-bar-close"
                onClick={() => { setManagedOwner(null); setActiveView('admin'); navigate('/admin'); }}
                title="Return to admin panel"
              >
                ← Back to Admin Panel
              </button>
            </div>
          </div>
        )}
        {renderView()}
      </main>
    </div>
  );
}
